/* Trace panel — live Developer Console output during product configuration */

import { state, esc } from './state.js';
import { api } from './api.js';
import { expandZone } from './layout-manager.js';
import { resolveTraceTarget } from './trace-link.js';
import { openFileTab, openSearch } from './search.js';
import { highlightTraceLine } from './monaco-editor.js';

const MAX_ACTIONS = 50;
const GLOBAL_PANEL_ID = 'global';
let _firstBatchDone = false;

const TERMINAL_ICON = `<i class="ic ic-terminal-square"></i>`;

function stripActionPrefix(name) {
  if (!name) return "Action";
  return name.replace(/^\/api\/rd\/v\d+\//, "")
             .replace(/^\/api\//, "")
             .replace(/^\//, "");
}

function fmtDuration(ms) {
  if (ms == null || ms === 0) return "";
  return ms < 1000 ? `${Math.round(ms)}ms` : `${(ms / 1000).toFixed(2)}s`;
}

function fmtTime(ts) {
  if (!ts) return "";
  try {
    const d = new Date(ts);
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" });
  } catch { return ""; }
}

// ---------------------------------------------------------------------------
// Panel creation
// ---------------------------------------------------------------------------

export function createTracePanel(configId) {
  const panel = document.createElement("div");
  panel.className = "cfg-trace-panel";
  panel.id = `cfg-trace-panel-${configId}`;
  panel.dataset.configId = configId;

  // Toggle bar (always visible)
  const toggle = document.createElement("div");
  toggle.className = "cfg-trace-toggle";

  const toggleBtn = document.createElement("button");
  toggleBtn.className = "cfg-trace-toggle-btn";
  toggleBtn.innerHTML = `${TERMINAL_ICON} Dev Console <span class="cfg-trace-badge" hidden>0</span>`;
  toggleBtn.addEventListener("click", () => _togglePanel(panel));
  toggle.appendChild(toggleBtn);

  const controls = document.createElement("div");
  controls.className = "cfg-trace-controls";
  controls.hidden = true;

  const filterGroup = document.createElement("div");
  filterGroup.className = "cfg-trace-filter-group";
  for (const mode of ["script", "all"]) {
    const btn = document.createElement("button");
    btn.className = "cfg-trace-filter-btn" + (mode === state.cfgTraceFilter ? " active" : "");
    btn.textContent = mode === "script" ? "Scripts" : "All";
    btn.dataset.filter = mode;
    btn.addEventListener("click", () => _switchFilter(panel, mode));
    filterGroup.appendChild(btn);
  }
  controls.appendChild(filterGroup);

  // Label filter dropdown — sits between mode buttons and clear
  const labelFilterEl = _createLabelFilterEl(panel);
  controls.appendChild(labelFilterEl);

  const clearBtn = document.createElement("button");
  clearBtn.className = "cfg-trace-clear-btn";
  clearBtn.textContent = "Clear";
  clearBtn.addEventListener("click", () => clearTraces(configId));
  controls.appendChild(clearBtn);

  toggle.appendChild(controls);
  panel.appendChild(toggle);

  // Body (hidden until toggled)
  const body = document.createElement("div");
  body.className = "cfg-trace-body";
  body.hidden = true;

  const empty = document.createElement("div");
  empty.className = "cfg-trace-empty";
  empty.textContent = "No traces yet\u2026 interact with the product to see script output.";
  body.appendChild(empty);

  panel.appendChild(body);

  return panel;
}

/**
 * Create the singleton trace panel and mount it into the given container.
 * Called once at app init from main.js (v1) or lazily on first Console visit
 * (v2, shell-v2.js's showViewV2() "console" branch, via _populateConsolePanel()).
 */
export function initGlobalTracePanel(container) {
  const panel = createTracePanel(GLOBAL_PANEL_ID);

  // Global panel lives in the Console tab — always visible, no toggle needed
  const body = panel.querySelector(".cfg-trace-body");
  if (body) body.hidden = false;
  const controls = panel.querySelector(".cfg-trace-controls");
  if (controls) controls.hidden = false;
  state.cfgTracePanelOpen = true;
  panel.classList.add("open");

  // Remove max-height constraint — bottom panel handles its own scrolling
  if (body) body.style.maxHeight = "none";

  container.appendChild(panel);

  // Replay any traces already buffered in state.cfgTraces (M12 fix round,
  // MAJOR-1): in v2 the Console is a lazily-mounted nav view, so traces that
  // arrive via addTraces() before the user's first visit are buffered into
  // state.cfgTraces but there is no DOM to render them into yet -- without a
  // replay here, that history is silently lost the moment this panel is
  // finally created. rerenderTraces() unconditionally rebuilds the body from
  // state.cfgTraces (declared further down this file; function declarations
  // are hoisted, so calling it here is safe), so this is idempotent and safe
  // to call every time this function runs -- including v1, where it runs once
  // at boot with an always-empty buffer (the WS handler isn't even wired yet
  // at that point in init()), so it just re-renders the same empty-state
  // placeholder createTracePanel() already produced: no double-render, no
  // behavior change for v1.
  rerenderTraces();
  return panel;
}

// ---------------------------------------------------------------------------
// Add traces
// ---------------------------------------------------------------------------

export function addTraces(configId, traceActions) {
  if (!traceActions || traceActions.length === 0) return;

  // Buffer into state FIRST, unconditionally (M12 fix round, MAJOR-1): this
  // must happen even when the Console panel doesn't exist yet in the DOM (v2:
  // the panel is created lazily, on first Console visit -- see
  // initGlobalTracePanel()'s replay above). Previously the panel-existence
  // check below returned before this buffering ran, so any traces that
  // arrived before the first Console visit in v2 were dropped forever, not
  // just left unrendered. state.cfgTraces is a per-session proxied field
  // (state.js's PER_SESSION_FIELDS) so this always targets the correct
  // (active) session's buffer, exactly as before.
  state.cfgTraces = state.cfgTraces.concat(traceActions);
  // Cap at MAX_ACTIONS
  if (state.cfgTraces.length > MAX_ACTIONS) {
    state.cfgTraces = state.cfgTraces.slice(-MAX_ACTIONS);
  }

  const panel = document.getElementById(`cfg-trace-panel-${GLOBAL_PANEL_ID}`);
  if (!panel) return;  // no DOM yet (v2, Console not visited) — state is buffered above; replayed on mount

  const body = panel.querySelector(".cfg-trace-body");
  const empty = body.querySelector(".cfg-trace-empty");
  if (empty) empty.remove();

  // Build DOM for new actions (newest first, with arrival animation)
  const firstChild = body.firstChild;
  for (let i = traceActions.length - 1; i >= 0; i--) {
    const actionEl = _buildActionEl(traceActions[i]);
    if (_firstBatchDone) {
      actionEl.classList.add("cfg-trace-new");
      setTimeout(() => actionEl.classList.remove("cfg-trace-new"), 1300);
    }
    body.insertBefore(actionEl, firstChild);
  }
  _firstBatchDone = true;

  // Trim oldest DOM nodes (at the bottom)
  const actionEls = body.querySelectorAll(".cfg-trace-action");
  if (actionEls.length > MAX_ACTIONS) {
    const excess = actionEls.length - MAX_ACTIONS;
    for (let i = actionEls.length - excess; i < actionEls.length; i++) actionEls[i].remove();
  }

  // Update badge — use API total_lines when available (raw count before
  // filtering/merging), fall back to processed trace count.
  const totalLines = state.cfgTraces.reduce(
    (s, a) => s + (a.total_lines || (a.traces ? a.traces.length : 0)), 0
  );
  _updateBadge(panel, totalLines);

  // Auto-scroll if panel is open and user hasn't scrolled up
  if (state.cfgTracePanelOpen && body.scrollHeight > body.clientHeight) {
    const nearBottom = body.scrollHeight - body.scrollTop - body.clientHeight < 60;
    if (nearBottom) {
      requestAnimationFrame(() => { body.scrollTop = body.scrollHeight; });
    }
  }

  // Auto-expand bottom zone when traces arrive (v1 only). v2's Console is a
  // NAV_MODEL view (Operate group) the user reaches by clicking a different
  // nav item, not the v1 collapsible bottom zone -- there is no zone to
  // expand there (M12 fix round, MAJOR-2). Calling expandZone('bottom')
  // unconditionally here was actively harmful in v2: layout-manager.js's zone
  // system is never initialized there (initLayout() only runs from v1's
  // init()), so state.bottomZoneVisible stays permanently false and this
  // branch fired on EVERY trace batch once the Console had been visited once
  // -- each call's _saveLayout() persists v2's untouched zone defaults into
  // localStorage['agentcpq_layout_v2'], the SAME key v1 reads, silently
  // clobbering the user's real saved v1 layout. Same #appV2-presence idiom
  // used elsewhere in this codebase for v1-vs-v2 branching (main.js's
  // _populateConsolePanel(), configurator.js's mountQuoteInPanel()).
  const isV2 = !!document.getElementById("appV2");
  if (!isV2 && !state.bottomZoneVisible) {
    expandZone('bottom');
  }
}

/**
 * Rebuild the global trace panel body from `state.cfgTraces`. `cfgTraces` is
 * per-session and NOT signal-backed, so when the active session changes the
 * panel must be re-rendered explicitly (addTraces only appends incrementally).
 */
export function rerenderTraces() {
  const panel = document.getElementById(`cfg-trace-panel-${GLOBAL_PANEL_ID}`);
  if (!panel) return;
  const body = panel.querySelector(".cfg-trace-body");
  if (!body) return;
  body.innerHTML = "";
  const traces = state.cfgTraces || [];
  if (traces.length === 0) {
    const empty = document.createElement("div");
    empty.className = "cfg-trace-empty";
    empty.textContent = "No traces yet… interact with the product to see script output.";
    body.appendChild(empty);
  } else {
    // newest-first (matches addTraces' insert order)
    for (let i = traces.length - 1; i >= 0; i--) {
      body.appendChild(_buildActionEl(traces[i]));
    }
  }
  const totalLines = traces.reduce(
    (s, a) => s + (a.total_lines || (a.traces ? a.traces.length : 0)), 0,
  );
  _updateBadge(panel, totalLines);
}

// ---------------------------------------------------------------------------
// Clear / Destroy
// ---------------------------------------------------------------------------

export function clearTraces(configId) {
  state.cfgTraces = [];
  _firstBatchDone = false;
  const panel = document.getElementById(`cfg-trace-panel-${GLOBAL_PANEL_ID}`);
  if (!panel) return;

  const body = panel.querySelector(".cfg-trace-body");
  body.innerHTML = "";
  const empty = document.createElement("div");
  empty.className = "cfg-trace-empty";
  empty.textContent = "No traces yet\u2026 interact with the product to see script output.";
  body.appendChild(empty);

  _updateBadge(panel, 0);
}

export function destroyTracePanel(configId) {
  // Global panel is permanent — never destroy it
  if (configId === GLOBAL_PANEL_ID) return;
  const panel = document.getElementById(`cfg-trace-panel-${configId}`);
  if (!panel) return;
  const lf = panel.querySelector(".cfg-label-filter");
  if (lf) {
    if (lf._stopTracking) lf._stopTracking();
    if (lf._hideLabel) document.removeEventListener("click", lf._hideLabel);
    if (lf._dropPanel) lf._dropPanel.remove();
  }
  panel.remove();
}

// ---------------------------------------------------------------------------
// Hierarchy tree builder — builds from HierarchyId (dotted notation)
// ---------------------------------------------------------------------------

/**
 * Build a tree of nodes from flat trace lines using their hierarchy_id.
 *
 * Each HierarchyId like "3.1.5" maps to a tree path.  [S]/[F] merging is
 * already done on the backend — each hierarchy_id appears at most once.
 *
 * Returns an array of root nodes, each: { line, children: [] }
 */
function _buildHierarchyTree(traceLines) {
  const nodeMap = new Map();  // hierarchy_id → { line, children: [] }

  // Create nodes for every line
  for (const line of traceLines) {
    const hid = line.hierarchy_id;
    if (!hid) continue;
    nodeMap.set(hid, { line, children: [] });
  }

  // Synthesize missing intermediate parents (defense-in-depth — backend
  // already does this, but protects against older backends or edge cases).
  const allHids = new Set(nodeMap.keys());
  for (const hid of [...allHids]) {
    const parts = hid.split(".");
    for (let i = 1; i < parts.length; i++) {
      const ancestor = parts.slice(0, i).join(".");
      if (!nodeMap.has(ancestor)) {
        nodeMap.set(ancestor, {
          line: { hierarchy_id: ancestor, message: "", full_message: "",
                  duration_ms: 0, logger: "", level: "", labels: "",
                  row_number: 0, synthetic: true },
          children: [],
        });
      }
    }
  }

  // Build parent-child relationships
  const roots = [];
  // Sort by hierarchy_id so parents are encountered consistently
  const sortedHids = [...nodeMap.keys()].sort(_cmpHierarchyId);

  for (const hid of sortedHids) {
    const node = nodeMap.get(hid);
    const dotIdx = hid.lastIndexOf(".");
    if (dotIdx === -1) {
      roots.push(node);
    } else {
      const parentHid = hid.substring(0, dotIdx);
      const parent = nodeMap.get(parentHid);
      if (parent) {
        parent.children.push(node);
      } else {
        // Orphan (parent was filtered out) — promote to root
        roots.push(node);
      }
    }
  }

  return roots;
}

// ---------------------------------------------------------------------------
// Label filter helpers
// ---------------------------------------------------------------------------

function _labelCls(label) {
  return label.toLowerCase().replace(/[^a-z0-9]/g, "");
}

function _collectLabels() {
  const seen = new Set();
  for (const action of state.cfgTraces) {
    for (const ln of (action.traces || [])) {
      const lbl = ln.labels || "";
      if (lbl) lbl.split(",").forEach(l => { const t = l.trim(); if (t) seen.add(t); });
    }
  }
  return [...seen].sort();
}

function _filterByLabel(traces, label) {
  if (!label) return traces;
  const keepHids = new Set();
  for (const ln of traces) {
    if ((ln.labels || "").split(",").map(s => s.trim()).includes(label)) {
      const parts = (ln.hierarchy_id || "").split(".");
      for (let i = 0; i < parts.length; i++) keepHids.add(parts.slice(0, i + 1).join("."));
    }
  }
  return traces.filter(ln => keepHids.has(ln.hierarchy_id));
}

function _createLabelFilterEl(panel) {
  const wrapper = document.createElement("div");
  wrapper.className = "cfg-label-filter";

  const btn = document.createElement("button");
  btn.className = "cfg-label-filter-btn";
  btn.innerHTML = `<span class="cfg-label-filter-text">Labels</span><span class="cfg-label-filter-arrow">▾</span>`;

  // Append to body so it escapes any CSS transform / overflow:hidden ancestor.
  const dropPanel = document.createElement("div");
  dropPanel.className = "cfg-label-filter-panel";
  dropPanel.hidden = true;
  document.body.appendChild(dropPanel);

  // RAF tracking loop — keeps the panel positioned directly under the button
  // even when the page scrolls (position:fixed coords are set every frame).
  let _rafId = null;
  function _startTracking() {
    function track() {
      if (dropPanel.hidden) { _rafId = null; return; }
      const r = btn.getBoundingClientRect();
      dropPanel.style.top  = `${r.bottom + 4}px`;
      dropPanel.style.left = `${r.left}px`;
      _rafId = requestAnimationFrame(track);
    }
    if (_rafId) cancelAnimationFrame(_rafId);
    track();
  }
  function _stopTracking() {
    if (_rafId) { cancelAnimationFrame(_rafId); _rafId = null; }
  }

  btn.addEventListener("click", (e) => {
    e.stopPropagation();
    if (dropPanel.hidden) {
      _populateLabelDropdown(dropPanel, btn, panel);
      dropPanel.hidden = false;
      _startTracking();
    } else {
      dropPanel.hidden = true;
      _stopTracking();
    }
  });

  // Close when clicking outside — must check both wrapper and detached dropPanel
  const _hideLabel = (e) => {
    if (!wrapper.contains(e.target) && !dropPanel.contains(e.target)) {
      dropPanel.hidden = true;
      _stopTracking();
    }
  };
  document.addEventListener("click", _hideLabel);
  wrapper._hideLabel = _hideLabel;
  wrapper._dropPanel = dropPanel;
  wrapper._stopTracking = _stopTracking; // for destroyTracePanel cleanup

  wrapper.appendChild(btn);
  return wrapper;
}

function _populateLabelDropdown(dropPanel, btn, panel) {
  dropPanel.innerHTML = "";
  const labels = _collectLabels();

  const allOpt = document.createElement("div");
  allOpt.className = "cfg-label-filter-opt" + (!state.cfgLabelFilter ? " active" : "");
  allOpt.textContent = "All labels";
  allOpt.addEventListener("click", (e) => {
    e.stopPropagation();
    state.cfgLabelFilter = "";
    dropPanel.hidden = true;
    _updateLabelBtn(btn);
    _rerenderTraces(panel);
  });
  dropPanel.appendChild(allOpt);

  for (const label of labels) {
    const opt = document.createElement("div");
    opt.className = "cfg-label-filter-opt" + (state.cfgLabelFilter === label ? " active" : "");
    const badge = document.createElement("span");
    badge.className = `cfg-trace-label cfg-trace-label-${_labelCls(label)}`;
    badge.textContent = label;
    opt.appendChild(badge);
    opt.addEventListener("click", (e) => {
      e.stopPropagation();
      state.cfgLabelFilter = label;
      dropPanel.hidden = true;
      _updateLabelBtn(btn);
      _rerenderTraces(panel);
    });
    dropPanel.appendChild(opt);
  }
}

function _updateLabelBtn(btn) {
  const arrow = `<span class="cfg-label-filter-arrow">▾</span>`;
  if (state.cfgLabelFilter) {
    const cls = _labelCls(state.cfgLabelFilter);
    btn.innerHTML = `<span class="cfg-trace-label cfg-trace-label-${cls}">${esc(state.cfgLabelFilter)}</span>${arrow}`;
    btn.classList.add("active");
  } else {
    btn.innerHTML = `<span class="cfg-label-filter-text">Labels</span>${arrow}`;
    btn.classList.remove("active");
  }
}

function _rerenderTraces(panel) {
  const body = panel.querySelector(".cfg-trace-body");
  body.querySelectorAll(".cfg-trace-action").forEach(el => el.remove());
  const empty = body.querySelector(".cfg-trace-empty");
  if (empty) empty.remove();
  if (state.cfgTraces.length === 0) {
    const emptyEl = document.createElement("div");
    emptyEl.className = "cfg-trace-empty";
    emptyEl.textContent = "No traces yet\u2026 interact with the product to see script output.";
    body.appendChild(emptyEl);
  } else {
    for (let i = state.cfgTraces.length - 1; i >= 0; i--) body.appendChild(_buildActionEl(state.cfgTraces[i]));
  }
  const totalLines = state.cfgTraces.reduce(
    (s, a) => s + (a.total_lines || (a.traces ? a.traces.length : 0)), 0
  );
  _updateBadge(panel, totalLines);
}

/** Compare two dotted hierarchy IDs numerically: "3.1.5" < "3.1.10" < "3.2" */
function _cmpHierarchyId(a, b) {
  const ap = a.split(".").map(Number);
  const bp = b.split(".").map(Number);
  for (let i = 0; i < Math.max(ap.length, bp.length); i++) {
    const av = ap[i] || 0, bv = bp[i] || 0;
    if (av !== bv) return av - bv;
  }
  return 0;
}

// ---------------------------------------------------------------------------
// Client-side script filter — mirrors backend filter_mode="script" logic
// ---------------------------------------------------------------------------

/**
 * Keep only script-related trace lines plus all ancestor nodes so the
 * hierarchy tree stays connected.  Matches the backend criteria in
 * configurator_session.py fetch_new_with_details().
 */
function _filterScriptTraces(traces) {
  const scriptHids = new Set();
  for (const ln of traces) {
    const lgr = ln.logger || "";
    const lvl = ln.level || "";
    const lbl = ln.labels || "";
    if (lgr.includes("Script") || lgr.includes("CPQScriptEngine")
        || lvl.includes("Trace H")
        || lvl.includes("Error")
        || lbl.includes("Script") || lbl.includes("ScriptEvent")
        || lbl.includes("ScriptError")) {
      const parts = (ln.hierarchy_id || "").split(".");
      for (let i = 0; i < parts.length; i++) {
        scriptHids.add(parts.slice(0, i + 1).join("."));
      }
    }
  }
  return traces.filter(ln => scriptHids.has(ln.hierarchy_id));
}

// ---------------------------------------------------------------------------
// Internals
// ---------------------------------------------------------------------------

function _togglePanel(panel) {
  state.cfgTracePanelOpen = !state.cfgTracePanelOpen;
  const body = panel.querySelector(".cfg-trace-body");
  const controls = panel.querySelector(".cfg-trace-controls");
  body.hidden = !state.cfgTracePanelOpen;
  controls.hidden = !state.cfgTracePanelOpen;
  panel.classList.toggle("open", state.cfgTracePanelOpen);

  // Stop badge pulse when opening
  if (state.cfgTracePanelOpen) {
    const badge = panel.querySelector(".cfg-trace-badge");
    if (badge) badge.classList.remove("pulse");
    // Scroll to bottom
    requestAnimationFrame(() => { body.scrollTop = body.scrollHeight; });
  }
}

function _switchFilter(panel, mode) {
  if (mode === state.cfgTraceFilter) return;
  state.cfgTraceFilter = mode;
  panel.querySelectorAll(".cfg-trace-filter-btn").forEach(btn => {
    btn.classList.toggle("active", btn.dataset.filter === mode);
  });
  _rerenderTraces(panel);
}

// ---------------------------------------------------------------------------
// Render a single tree node (recursive)
// ---------------------------------------------------------------------------

function _buildNodeEl(node, allLines) {
  const { line, children } = node;
  const hasChildren = children.length > 0;
  const dur = fmtDuration(line.duration_ms);
  const level = (line.level || "").replace("Trace ", "");
  const labels = line.labels || "";
  const isSynthetic = !!line.synthetic;
  const tooltip = line.full_message || line.message || "";

  // Display text: for synthetic container nodes with no message, show HID
  const displayText = line.message
    ? line.message
    : (isSynthetic && line.hierarchy_id ? `[${line.hierarchy_id}]` : "(unnamed)");

  // Decide label tag color
  let labelTag = "";
  if (labels) {
    const primary = labels.split(",")[0].trim();
    if (primary) {
      labelTag = `<span class="cfg-trace-label cfg-trace-label-${esc(primary.toLowerCase())}">${esc(primary)}</span>`;
    }
  }

  // trace→code: can this line jump to a source file (or a Find-in-Files)?
  const target = resolveTraceTarget(line, allLines);
  const openTitle = target
    ? (target.mode === "open"
        ? `Open ${target.scriptName}${target.line ? ` at line ${target.line}` : ""} in the editor`
        : `Find "${target.query}" in files`)
    : "";
  const openGlyph = target ? `<button class="cfg-trace-open">&lt;/&gt;</button>` : "";
  const wireOpen = (containerEl) => {
    const btn = containerEl.querySelector(".cfg-trace-open");
    if (!btn) return;
    // Set as a DOM property (not an HTML attribute) so quotes in the query
    // don't truncate the tooltip.
    btn.title = openTitle;
    btn.setAttribute("aria-label", openTitle);
    btn.addEventListener("click", (e) => { e.stopPropagation(); openTraceTarget(target); });
  };

  if (hasChildren) {
    // Collapsible parent node
    const el = document.createElement("div");
    el.className = "cfg-trace-group" + (isSynthetic ? " cfg-trace-synthetic" : "") + (target ? " cfg-trace-linkable" : "");

    const header = document.createElement("div");
    header.className = "cfg-trace-group-header";
    header.innerHTML = `
      <span class="cfg-trace-chevron">&#9656;</span>
      ${level ? `<span class="cfg-trace-level cfg-trace-level-${esc(level.toLowerCase())}">${esc(level)}</span>` : ""}
      ${labelTag}
      <span class="cfg-trace-group-text">${esc(displayText)}</span>
      <span class="cfg-trace-group-meta">
        ${openGlyph}
        <span class="cfg-trace-line-count">${children.length}</span>
        ${dur ? `<span class="cfg-trace-group-dur">${esc(dur)}</span>` : ""}
      </span>
    `;
    header.title = tooltip;

    let bodyBuilt = false;
    const bodyEl = document.createElement("div");
    bodyEl.className = "cfg-trace-group-body";
    bodyEl.hidden = true;

    header.addEventListener("click", () => {
      if (!bodyBuilt) {
        bodyBuilt = true;
        for (const child of children) {
          bodyEl.appendChild(_buildNodeEl(child, allLines));
        }
      }
      const wasHidden = bodyEl.hidden;
      bodyEl.hidden = !wasHidden;
      el.classList.toggle("expanded", wasHidden);
    });
    if (target) wireOpen(header);

    el.appendChild(header);
    el.appendChild(bodyEl);
    return el;
  } else {
    // Leaf node — simple trace line
    const el = document.createElement("div");
    el.className = "cfg-trace-line" + (isSynthetic ? " cfg-trace-synthetic" : "") + (target ? " cfg-trace-linkable" : "");
    el.title = tooltip;

    el.innerHTML = `
      <span class="cfg-trace-chevron"></span>
      ${level ? `<span class="cfg-trace-level cfg-trace-level-${esc(level.toLowerCase())}">${esc(level)}</span>` : ""}
      ${labelTag}
      <span class="cfg-trace-msg">${esc(displayText)}</span>
      ${openGlyph}
      ${dur ? `<span class="cfg-trace-dur">${esc(dur)}</span>` : ""}
    `;
    if (target) wireOpen(el);
    return el;
  }
}

// Open the resolved trace target: a source file at the error line, or — for a
// descriptive event-script name with no file — an honest Find-in-Files.
async function openTraceTarget(target) {
  if (!target) return;
  if (target.mode === "search") {
    openSearch();
    const inp = document.getElementById("searchInput");
    if (inp) {
      inp.value = target.query;
      inp.dispatchEvent(new Event("input", { bubbles: true }));
      inp.focus();
    }
    return;
  }
  const name = target.scriptName;
  const statuses = state.syncStatuses || [];
  const isCalc = statuses.some((s) => s.kind === "custom-calculations" && s.name === name);
  const path = isCalc ? `custom-calculations/${name}.py` : `global-scripts/${name}.py`;
  openFileTab(path, name, "python", target.line || undefined);
  if (target.line) setTimeout(() => highlightTraceLine(target.line), 150);
}

// ---------------------------------------------------------------------------
// Action element — one per HTTP request traced
// ---------------------------------------------------------------------------

function _buildActionEl(action) {
  const el = document.createElement("div");
  el.className = "cfg-trace-action";

  const allTraces = action.traces || [];
  let traces = state.cfgTraceFilter === "script"
    ? _filterScriptTraces(allTraces)
    : allTraces;
  if (state.cfgLabelFilter) traces = _filterByLabel(traces, state.cfgLabelFilter);
  const name = stripActionPrefix(action.action_name);
  const time = fmtTime(action.start_time);

  // Build hierarchy tree from trace lines
  const tree = _buildHierarchyTree(traces);
  const rootCount = tree.length;

  // Header
  const header = document.createElement("div");
  header.className = "cfg-trace-action-header";
  header.innerHTML = `
    <span class="cfg-trace-chevron">&#9656;</span>
    <span class="cfg-trace-action-name">${esc(name)}</span>
    <span class="cfg-trace-action-meta">
      <span class="cfg-trace-line-count">${traces.length} line${traces.length !== 1 ? "s" : ""}</span>
      ${time ? `<span class="cfg-trace-action-time">${esc(time)}</span>` : ""}
    </span>
  `;
  header.addEventListener("click", () => {
    const bodyEl = el.querySelector(".cfg-trace-action-body");
    const expanded = !bodyEl.hidden;
    bodyEl.hidden = expanded;
    el.classList.toggle("expanded", !expanded);
  });
  el.appendChild(header);

  // Body — render hierarchy tree
  const bodyEl = document.createElement("div");
  bodyEl.className = "cfg-trace-action-body";
  bodyEl.hidden = true;

  if (rootCount > 0) {
    for (const root of tree) {
      bodyEl.appendChild(_buildNodeEl(root, allTraces));
    }
  } else if (traces.length > 0) {
    // Fallback: flat rendering if no hierarchy_ids
    for (const line of traces) {
      bodyEl.appendChild(_buildNodeEl({ line, children: [] }, allTraces));
    }
  } else {
    const noLines = document.createElement("div");
    noLines.className = "cfg-trace-empty-action";
    noLines.textContent = "No trace lines";
    bodyEl.appendChild(noLines);
  }

  el.appendChild(bodyEl);
  return el;
}

function _updateBadge(panel, count) {
  const badge = panel.querySelector(".cfg-trace-badge");
  if (!badge) return;
  badge.textContent = String(count);
  badge.hidden = count === 0;

  // Pulse when new traces arrive while panel is closed
  if (count > 0 && !state.cfgTracePanelOpen) {
    badge.classList.remove("pulse");
    // Force reflow to restart animation
    void badge.offsetWidth;
    badge.classList.add("pulse");
  }
}
